import { Badge, BasePagination, ChakraSelect, HStack, IconButton, Text, Tooltip, VStack } from '@elkaso-app/web-design';
import { useDeleteGoogleLocationMappingApi } from 'apis/crm-google-locations/use-delete-google-location-mapping-api';
import { useGetCrmBrandLocationsSelectApi } from 'apis/crm-google-locations/use-get-crm-brand-locations-select-api';
import {
  getCrmGoogleLocationMappingsEndpointIdentifier,
  ICrmGoogleLocationMappingsData,
  useGetCrmGoogleLocationMappingsApi,
} from 'apis/crm-google-locations/use-get-crm-google-location-mappings-api';
import { useUpdateCrmGoogleLocationMappingApi } from 'apis/crm-google-locations/use-update-crm-google-location-mapping-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { CrmBrandFilterButton } from 'pages/crm-google-locations/components/filters/crm-brand-filter';
import { CrmGoogleLocationFilterButton } from 'pages/crm-google-locations/components/filters/crm-google-location-filter';
import React, { useEffect, useState } from 'react';
import { FiCheck, FiEdit3, FiTrash2 } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// Components for table cells
const GoogleLocationCell: React.FC<{
  name: string;
  address: string;
  type: string;
  businessStatus: string;
}> = ({ name, address, type, businessStatus }) => (
  <div style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8px 0' }}>
    <VStack align='start' spacing={2}>
      <Text fontWeight='semibold' fontSize='md' color='gray.800' lineHeight='1.2'>
        {name}
      </Text>
      <HStack spacing={2}>
        <Badge colorScheme='blue' variant='subtle' size='sm' borderRadius='md'>
          {type}
        </Badge>
        <Badge
          colorScheme={businessStatus === 'OPERATIONAL' ? 'green' : 'red'}
          variant='subtle'
          size='sm'
          borderRadius='md'>
          {businessStatus}
        </Badge>
      </HStack>
      <Text fontSize='xs' color='gray.600' noOfLines={1}>
        {address}
      </Text>
    </VStack>
  </div>
);

const BrandAndLocationMappingCell: React.FC<{
  brandName: string;
  brandId: number;
  mappingId: number;
  selectedBrandLocationId: number | null;
  mappedLocation: any;
  onBrandLocationChange: (mappingId: number, brandLocationId: number | null) => void;
}> = ({ brandName, brandId, mappingId, selectedBrandLocationId, mappedLocation, onBrandLocationChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: brandLocations, isLoading: isBrandLocationsLoading } = useGetCrmBrandLocationsSelectApi(
    {
      brandId,
      page: 1,
      limit: 100,
    },
    {
      enabled: isMenuOpen && !!brandId,
    }
  );

  // Format brand locations for ChakraSelect with custom option format
  const brandLocationOptions =
    brandLocations?.data?.map((location: any) => ({
      value: location.id,
      label: location.name,
      data: location, // Store full location data for custom rendering
    })) || [];

  const selectedOption = brandLocationOptions.find((option: any) => option.value === selectedBrandLocationId) || null;

  const handleLocationChange = (option: any) => {
    onBrandLocationChange(mappingId, option?.value || null);
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setIsMenuOpen(false); // Reset menu state
  };

  if (isEditing) {
    return (
      <div
        style={{
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '8px 0',
        }}>
        <VStack align='start' spacing={2}>
          <Text fontWeight='medium' fontSize='sm' color='gray.700'>
            {brandName}
          </Text>
          <div
            style={{
              width: '100%',
              minWidth: '280px',
              maxWidth: '280px',
            }}>
            <ChakraSelect
              value={selectedOption}
              onChange={handleLocationChange}
              options={brandLocationOptions || []}
              size='sm'
              placeholder='Search and select location...'
              isClearable
              isSearchable
              isLoading={isBrandLocationsLoading}
              menuPlacement='auto'
              menuPortalTarget={document.body}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
              autoFocus
              formatOptionLabel={(option: any) => (
                <div style={{ padding: '4px 0' }}>
                  <VStack align='start' spacing={1}>
                    <Text fontWeight='semibold' fontSize='sm' color='gray.800'>
                      📍 {option.label}
                    </Text>
                    <Text fontSize='xs' color='gray.600' noOfLines={1}>
                      {option.data.address}
                    </Text>
                    <HStack spacing={1}>
                      {option.data.geoLocation ? (
                        <Badge colorScheme='purple' variant='subtle' size='xs' borderRadius='md'>
                          {option.data.geoLocation.name}
                        </Badge>
                      ) : option.data.area ? (
                        <Badge colorScheme='purple' variant='subtle' size='xs' borderRadius='md'>
                          {option.data.area}
                        </Badge>
                      ) : null}
                    </HStack>
                  </VStack>
                </div>
              )}
            />
          </div>
        </VStack>
      </div>
    );
  }

  return (
    <div
      style={{ height: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '15px 0' }}>
      <VStack align='start' spacing={2}>
        <Text fontWeight='medium' fontSize='sm' color='gray.700'>
          🏢 {brandName}
        </Text>
        {mappedLocation ? (
          <HStack justify='space-between' width='100%'>
            <VStack align='start' spacing={1} flex={1}>
              <Text fontWeight='semibold' fontSize='sm' color='gray.800'>
                📍 {mappedLocation.name}
              </Text>
              <Text fontSize='xs' color='gray.600' noOfLines={1}>
                {mappedLocation.address}
              </Text>
              {mappedLocation.geoLocation ? (
                <Badge colorScheme='purple' variant='subtle' size='xs' borderRadius='md'>
                  {mappedLocation.geoLocation.name}
                </Badge>
              ) : mappedLocation.area ? (
                <Badge colorScheme='purple' variant='subtle' size='xs' borderRadius='md'>
                  {mappedLocation.area}
                </Badge>
              ) : null}
            </VStack>
            <IconButton
              aria-label='Edit location mapping'
              icon={<FiEdit3 />}
              size='xs'
              colorScheme='blue'
              variant='ghost'
              onClick={handleEditClick}
            />
          </HStack>
        ) : (
          <HStack justify='space-between' width='100%'>
            <Text fontSize='sm' color='gray.500' fontStyle='italic'>
              No location mapped
            </Text>
            <IconButton
              aria-label='Add location mapping'
              icon={<FiEdit3 />}
              size='xs'
              colorScheme='blue'
              variant='ghost'
              onClick={handleEditClick}
            />
          </HStack>
        )}
      </VStack>
    </div>
  );
};

const ConfidenceCell: React.FC<{
  confidence: number;
  mappingId: number;
  onConfidenceChange: (mappingId: number, confidence: number) => void;
}> = ({ confidence, mappingId, onConfidenceChange }) => {
  const confidenceOptions = [
    { value: 100, label: '100% - Perfect match' },
    { value: 90, label: '90% - Very confident' },
    { value: 80, label: '80% - Confident' },
    { value: 70, label: '70% - Somewhat confident' },
    { value: 60, label: '60% - Uncertain' },
  ];

  const selectedOption = confidenceOptions.find((option) => option.value === confidence) || null;

  return (
    <div
      style={{
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        width: '220px',
        minWidth: '220px',
        maxWidth: '220px',
        padding: '8px 0',
      }}>
      <div
        style={{
          width: '100%',
          minWidth: '220px',
          maxWidth: '220px',
        }}>
        <ChakraSelect
          value={selectedOption}
          onChange={(option: any) => onConfidenceChange(mappingId, option?.value || 60)}
          options={confidenceOptions}
          size='sm'
          menuPlacement='auto'
          menuPortalTarget={document.body}
        />
      </div>
    </div>
  );
};

const ActionsCell: React.FC<{
  mappingId: number;
  brandLocationId: number | null;
  confidence: number | null;
  isVerified: boolean;
  onApprove: (mappingId: number) => void;
  onDelete: (mappingId: number) => void;
}> = ({ mappingId, brandLocationId, confidence, isVerified, onApprove, onDelete }) => {
  const hasRequiredSelections = brandLocationId && confidence;

  const getTooltipLabel = () => {
    if (isVerified && hasRequiredSelections) return 'Update mapping';
    if (isVerified) return 'Select brand location and confidence to update';
    if (hasRequiredSelections) return 'Approve';
    if (!brandLocationId && !confidence) return 'Select brand location and confidence';
    if (!brandLocationId) return 'Select brand location';
    return 'Select confidence level';
  };

  return (
    <div
      style={{
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
        gap: '8px',
      }}>
      <Tooltip label={getTooltipLabel()}>
        <IconButton
          aria-label={isVerified ? 'Update mapping' : 'Approve mapping'}
          icon={<FiCheck />}
          size='sm'
          colorScheme='green'
          variant={isVerified ? 'solid' : 'outline'}
          isDisabled={!hasRequiredSelections}
          onClick={() => onApprove(mappingId)}
        />
      </Tooltip>
      {isVerified && (
        <Tooltip label='Delete mapping'>
          <IconButton
            aria-label='Delete mapping'
            icon={<FiTrash2 />}
            size='sm'
            colorScheme='red'
            variant='outline'
            onClick={() => onDelete(mappingId)}
          />
        </Tooltip>
      )}
    </div>
  );
};

const CrmGoogleLocationsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const updateMappingMutation = useUpdateCrmGoogleLocationMappingApi();
  const deleteMappingMutation = useDeleteGoogleLocationMappingApi();

  // State for filters and search

  // State for temporary changes
  const [tempBrandLocations, setTempBrandLocations] = useState<Record<number, number>>({});
  const [tempConfidenceScores, setTempConfidenceScores] = useState<Record<number, number>>({});

  const { isLoading, isFetching, data } = useGetCrmGoogleLocationMappingsApi();
  const { pageCount, total, data: mappingsData } = data ?? {};

  // Filter data based on search and filters
  const getFilteredData = () => {
    if (!mappingsData) return [];

    return mappingsData;
  };

  const filteredData = getFilteredData();

  const handleBrandLocationChange = (mappingId: number, brandLocationId: number | null) => {
    setTempBrandLocations((prev) => {
      if (brandLocationId === null) {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      }
      return {
        ...prev,
        [mappingId]: brandLocationId,
      };
    });
  };

  const handleConfidenceChange = (mappingId: number, confidence: number) => {
    setTempConfidenceScores((prev) => ({
      ...prev,
      [mappingId]: confidence,
    }));
  };

  const handleApprove = async (mappingId: number) => {
    const mapping = mappingsData?.find((m) => m.googleLocation.id === mappingId);
    if (!mapping) return;

    const brandLocationId = tempBrandLocations[mappingId] || mapping.location?.id || null;
    if (!brandLocationId) return;

    const confidencePercentage = tempConfidenceScores[mappingId] || mapping.googleLocation.mappingConfidence * 100;
    // Convert from percentage (0-100) to decimal (0.00-1.00)
    const confidenceScore = confidencePercentage / 100;

    try {
      await updateMappingMutation.mutateAsync({
        id: mappingId,
        body: {
          googleLocationId: mapping.googleLocation.id,
          brandId: mapping.brand.id,
          locationId: brandLocationId,
          mappingConfidence: confidenceScore,
          businessStatus: mapping.googleLocation.businessStatus,
        },
      });

      // Remove from temp state
      setTempBrandLocations((prev) => {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      });

      setTempConfidenceScores((prev) => {
        const newState = { ...prev };
        delete newState[mappingId];
        return newState;
      });

      // Invalidate and refetch
      queryClient.invalidateQueries([getCrmGoogleLocationMappingsEndpointIdentifier]);
    } catch (error) {
      console.error('Failed to update mapping:', error);
    }
  };

  const handleDelete = async (mappingId: number) => {
    try {
      await deleteMappingMutation.mutateAsync({
        googleLocationId: mappingId,
      });

      // Invalidate and refetch
      queryClient.invalidateQueries([getCrmGoogleLocationMappingsEndpointIdentifier]);
    } catch (error) {
      console.error('Failed to delete mapping:', error);
    }
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Google Locations'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Google Location'
            sortButtonComponent={<BaseSortButton sortBy='googleLocation.name' />}
            filterButtonComponent={<CrmGoogleLocationFilterButton />}
          />
        ),
        accessor: 'googleLocation',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={GoogleLocationCell} {...value} />,
      },
      {
        Header: (
          <TableHeader
            name='Brand & Location Mapping'
            sortButtonComponent={<BaseSortButton sortBy='brand.name' />}
            filterButtonComponent={<CrmBrandFilterButton />}
          />
        ),
        accessor: 'brandAndLocationMapping',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={BrandAndLocationMappingCell} {...value} />,
      },
      {
        Header: <TableHeader name='Confidence' />,
        accessor: 'confidence',
        width: '20%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ConfidenceCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '10%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      filteredData?.map((item: ICrmGoogleLocationMappingsData) => ({
        id: item.googleLocation.id,
        googleLocation: {
          name: item.googleLocation.name,
          address: item.googleLocation.address,
          type: item.googleLocation.type,
          businessStatus: item.googleLocation.businessStatus,
        },
        brandAndLocationMapping: {
          brandName: item.brand.name,
          brandId: item.brand.id,
          mappingId: item.googleLocation.id,
          selectedBrandLocationId: tempBrandLocations[item.googleLocation.id] || item.location?.id || null,
          mappedLocation: item.location,
          onBrandLocationChange: handleBrandLocationChange,
        },
        confidence: {
          confidence: tempConfidenceScores[item.googleLocation.id] || item.googleLocation.mappingConfidence * 100,
          mappingId: item.googleLocation.id,
          onConfidenceChange: handleConfidenceChange,
        },
        actions: {
          mappingId: item.googleLocation.id,
          brandLocationId: tempBrandLocations[item.googleLocation.id] || item.location?.id || null,
          confidence: tempConfidenceScores[item.googleLocation.id] || item.googleLocation.mappingConfidence * 100,
          isVerified: !!item.location,
          onApprove: handleApprove,
          onDelete: handleDelete,
        },
      })) || [],
    [filteredData, tempBrandLocations, tempConfidenceScores]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='CRM Google Locations' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={tableData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};

export default CrmGoogleLocationsListScreen;
